import { useState } from "react";
import { fornitoreRiordinoSost } from "../riordinaSostApi";
import type { Order, SupplierReplacementEntry } from "../types";
import { ActionButton } from "./ActionButton";
import { IconCart, IconCheck } from "./icons";
import { RiordinaSostDialog } from "./RiordinaSostDialog";

/** Registro sostituzioni fornitore (Itamoby/Ityhome) dal raw ordine:
 *  popolato dal backend quando arrivano mail con "sost" nel subject.
 *
 *  ``costTotal`` viene dal backend e NON e' la somma di ``entries``: le entry
 *  AS manuali sono salvate IVATE e il totale e' netto (``/1.22`` in
 *  ``orders/replacements.py``, fonte unica cosi' card e KPI BI leggono lo
 *  stesso numero). Sommare le entry qui, o riportare il ``/1.22`` in TS,
 *  darebbe un numero diverso e una seconda definizione dello stesso
 *  invariante: le entry si mostrano COME REGISTRATE, con l'etichetta che dice
 *  se sono nette o ivate (``gross``). */
export function supplierReplacements(order: Order): {
  count: number;
  costTotal: number;
  entries: SupplierReplacementEntry[];
} {
  const raw = order.raw ?? {};
  // Cast e non validazione: e' JSONB scritto da due writer Python, nulla ne
  // garantisce la forma → il tipo dichiara opzionale tutto tranne cost/at e
  // ``cost: number | null`` (caso reale: sost annunciata prima dell'importo).
  const entries: SupplierReplacementEntry[] = Array.isArray(raw.supplier_replacements)
    ? (raw.supplier_replacements as SupplierReplacementEntry[])
    : [];
  const totalRaw = raw.supplier_replacements_cost_total;
  return {
    count: entries.length,
    costTotal: typeof totalRaw === "number" ? totalRaw : 0,
    entries,
  };
}

/** Tasto «Riordina» (Davide 2026-09-24): rifa' l'ordine al fornitore come
 *  sostituzione, numero ``S-<ref>`` (poi ``S2-``…). Esiste solo dove OM sa ordinare e
 *  l'ordine originale e' gia' partito (``fornitoreRiordinoSost``). Ha preso il
 *  posto di «Rispedisci AS»: stesso gesto, ora per tutti i fornitori. */
export function RiordinaSostButton({
  order,
  onDone,
  compact = false,
}: {
  order: Order;
  onDone?: () => void;
  /** Colonna Azioni della card lista: tasto verticale con micro-etichetta.
   *  Senza, il pill testuale della pagina ordine. */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [inviato, setInviato] = useState<string | null>(null);
  const fornitore = fornitoreRiordinoSost(order);
  if (!fornitore) return null;

  const title = inviato
    ? `Sostituzione ${inviato} ordinata a ${fornitore.nome}`
    : `Riordina a ${fornitore.nome} come sostituzione (numero S-${order.id}, poi S2-…)`;
  function apri(e: React.MouseEvent) {
    e.stopPropagation();
    setOpen(true);
  }

  return (
    <>
      {compact ? (
        <ActionButton
          tone="amber"
          icon={
            <IconCart className="w-[15px] h-[15px] fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]" />
          }
          label={inviato ? "Inviata" : "Riordina"}
          ariaLabel={`Riordina come sostituzione ordine ${order.id}`}
          title={title}
          onClick={apri}
        />
      ) : (
        <button
          type="button"
          data-no-open
          title={title}
          onClick={apri}
          className={`inline-flex min-h-[38px] shrink-0 items-center gap-1.5 rounded-md border px-3 text-[13px] font-semibold text-ink transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 ${
            inviato ? "border-green bg-green-soft" : "border-violet bg-violet-soft hover:bg-card"
          }`}
        >
          {inviato ? (
            <>
              <IconCheck className="h-3.5 w-3.5 fill-none stroke-green stroke-[3] [stroke-linecap:round] [stroke-linejoin:round]" />
              {inviato} ordinata
            </>
          ) : (
            <>
              <IconCart className="h-[15px] w-[15px] fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]" />
              Riordina come sostituzione
            </>
          )}
        </button>
      )}
      {open && (
        <RiordinaSostDialog
          orderRef={order.id}
          fornitore={fornitore}
          defaultWhat={order.productName}
          onCancel={() => setOpen(false)}
          onDone={(riferimento) => {
            setInviato(riferimento);
            setOpen(false);
            onDone?.();
          }}
        />
      )}
    </>
  );
}
